'use client';

import { useTranslations } from 'next-intl';
import { ArrowLeft, Loader2, Package, ScanLine, User } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface AssignmentConfirmFooterProps {
  selectedContainerCount: number;
  selectedScanner: string | null; // scanner name
  selectedOperator: string | null; // operator name
  disabled: boolean;
  onConfirm: () => void;
  isConfirming: boolean;
}

export function AssignmentConfirmFooter({
  selectedContainerCount,
  selectedScanner,
  selectedOperator,
  disabled,
  onConfirm,
  isConfirming
}: AssignmentConfirmFooterProps) {
  const t = useTranslations('handoverScanning');

  return (
    <div className='bg-background sticky bottom-0 z-50 border-t p-4'>
      <div className='mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4'>
        {/* Summary items */}
        <div className='flex flex-wrap items-center gap-4'>
          {/* Containers */}
          <div className='flex items-center gap-1.5'>
            <Package className='text-muted-foreground h-4 w-4' />
            <span className='text-muted-foreground text-sm'>
              {t('selectedContainers')}:
            </span>
            <Badge variant='secondary'>
              {t('footerContainers', { count: selectedContainerCount })}
            </Badge>
          </div>

          {/* Scanner */}
          <div className='flex items-center gap-1.5'>
            <ScanLine className='text-muted-foreground h-4 w-4' />
            <span className='text-muted-foreground text-sm'>
              {t('selectedScanner')}:
            </span>
            {selectedScanner ? (
              <span className='text-sm font-medium'>{selectedScanner}</span>
            ) : (
              <span className='text-muted-foreground text-sm italic'>
                {t('notSelected')}
              </span>
            )}
          </div>

          {/* Operator */}
          <div className='flex items-center gap-1.5'>
            <User className='text-muted-foreground h-4 w-4' />
            <span className='text-muted-foreground text-sm'>
              {t('selectedOperator')}:
            </span>
            {selectedOperator ? (
              <span className='text-sm font-medium'>{selectedOperator}</span>
            ) : (
              <span className='text-muted-foreground text-sm italic'>
                {t('notSelected')}
              </span>
            )}
          </div>
        </div>

        {/* Confirm button */}
        <Button
          onClick={onConfirm}
          disabled={disabled || isConfirming}
          className={cn('shrink-0')}
        >
          {isConfirming ? (
            <Loader2 className='me-2 h-4 w-4 animate-spin' />
          ) : (
            <ArrowLeft className='me-2 h-4 w-4 rtl:rotate-180' />
          )}
          {t('confirmAssignment')}
        </Button>
      </div>
    </div>
  );
}
